import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import client from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';

function StatusBadge({ s }) {
  if (s === 'approved') return <Badge className="bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>;
  if (s === 'rejected') return <Badge className="bg-rose-400/15 text-rose-300 border border-rose-400/30">Rejected</Badge>;
  return <Badge className="bg-amber-400/15 text-amber-300 border border-amber-400/30"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
}

export default function PartnerDashboard() {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [deals, setDeals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', industry: 'Seaport', platform: 'Industrial AI Platform', estimated_value: '', notes: '' });

  const load = () => client.get('/deals').then((r) => setDeals(r.data)).catch(() => {});

  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return <div className="p-10 text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'partner') return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.post('/deals', { ...form, estimated_value: parseFloat(form.estimated_value || '0') });
      toast({ title: 'Deal submitted', description: 'Awaiting approval.' });
      setShowForm(false);
      setForm({ customer_name: '', customer_email: '', industry: 'Seaport', platform: 'Industrial AI Platform', estimated_value: '', notes: '' });
      load();
    } catch (err) {
      toast({ title: 'Failed to create deal', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setSaving(false); }
  };

  const stats = {
    total: deals.length,
    approved: deals.filter((d) => d.status === 'approved').length,
    pending: deals.filter((d) => d.status === 'pending').length,
    value: deals.reduce((s, d) => s + (d.estimated_value || 0), 0),
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">Partner Portal</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Welcome, {user.name}</h1>
          <div className="mt-2 text-sm text-slate-400">{user.company || 'Independent partner'} · {user.company_email}</div>
          {!user.partner_approved && (
            <div className="mt-3 inline-flex items-center gap-2 text-amber-300 text-sm bg-amber-400/10 border border-amber-400/30 rounded-md px-3 py-1.5">
              <AlertTriangle className="w-4 h-4" /> Partner approval pending — you can still register deals.
            </div>
          )}
        </div>
        <Button onClick={() => setShowForm((v) => !v)} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900">
          <Plus className="w-4 h-4 mr-1" /> {showForm ? 'Close' : 'Register deal'}
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mt-8">
        {[
          { l: 'Total deals', v: stats.total },
          { l: 'Approved', v: stats.approved },
          { l: 'Pending', v: stats.pending },
          { l: 'Pipeline value', v: `$${(stats.value / 1000).toFixed(1)}k` },
        ].map((s) => (
          <div key={s.l} className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
            <div className="text-xs text-slate-400">{s.l}</div>
            <div className="font-display text-2xl font-bold text-cyan-300 mt-1">{s.v}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 grid md:grid-cols-2 gap-4">
          <div>
            <Label>Customer name *</Label>
            <Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
          </div>
          <div>
            <Label>Customer email *</Label>
            <Input type="email" required value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
          </div>
          <div>
            <Label>Industry</Label>
            <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
              <SelectTrigger className="bg-black/30 border-white/10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Seaport', 'Warehouse', 'Airport', 'Enterprise'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Platform</Label>
            <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
              <SelectTrigger className="bg-black/30 border-white/10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Industrial AI Platform', 'VisoPercept', 'TrackSure', 'Digital Twin'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Estimated value (USD)</Label>
            <Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
          </div>
          <div className="md:col-span-2">
            <Button disabled={saving} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Submit for approval
            </Button>
          </div>
        </form>
      )}

      <div className="mt-10">
        <h2 className="font-display text-xl font-semibold mb-4">Registered deals</h2>
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-slate-400 text-left">
              <tr>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Industry</th>
                <th className="px-4 py-3">Platform</th>
                <th className="px-4 py-3">Value</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {deals.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No deals yet. Register your first deal above.</td></tr>
              )}
              {deals.map((d) => (
                <tr key={d.id} className="border-t border-white/5">
                  <td className="px-4 py-3"><div className="font-medium">{d.customer_name}</div><div className="text-xs text-slate-500">{d.customer_email}</div></td>
                  <td className="px-4 py-3 text-slate-300">{d.industry}</td>
                  <td className="px-4 py-3 text-slate-300">{d.platform}</td>
                  <td className="px-4 py-3 text-slate-300">${(d.estimated_value || 0).toLocaleString()}</td>
                  <td className="px-4 py-3"><StatusBadge s={d.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
